import { useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { AppState } from '@/types';

export function useStatePersistence(
  state: AppState,
  setAppState: (state: AppState) => void,
  isInitialized: boolean,
  loading: boolean
) {
  const { user } = useAuth();

  // Save state changes to Supabase (only after initialization and prevent infinite loops)
  useEffect(() => {
    // Check if data clearing is in progress - CRITICAL: Never save during clearing
    const isClearing = (window as any).clearingData;
    if (isClearing) {
      console.log('🚫 Skipping save - data clearing in progress');
      return;
    }
    
    // Only save if user exists, not loading, and is initialized
    // CRITICAL: Don't save if we're still in the loading/sync process where state might be stale
    if (user && !loading && isInitialized && state.isAuthenticated) {
      console.log('🔄 Save trigger - state.totalPoints:', state.totalPoints, 'currentWeekPoints:', state.currentWeekPoints, 'activities:', state.activities.length);
      
      // Prevent saving during initial data load when state might be stale
      // But allow saving if user has just logged their first activity
      const hasNewActivities = state.activities.length > 0;
      const isLikelyStaleState = !hasNewActivities && state.totalPoints === 0 && isInitialized;
      
      if (isLikelyStaleState) {
        console.log('⏸️ Skipping save - likely stale state during data load (no activities or points)');
        return;
      }
      
      // Only save specific state changes to prevent loops
      const saveData = async () => {
        try {
          // Double-check clearing flag before actual save
          if ((window as any).clearingData) {
            console.log('🚫 Aborting save - clearing started during debounce');
            return;
          }
          console.log('💾 Attempting to save state to Supabase...');
          await setAppState(state);
          console.log('✅ State saved to Supabase successfully');
        } catch (error) {
          console.error('❌ Failed to save state to Supabase:', error);
          // Don't throw error to prevent infinite loops
        }
      };
      
      // Debounce saves to prevent rapid fire saves (longer delay for reset operations)
      const timeoutId = setTimeout(saveData, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [
    // Only trigger saves on actual DATA changes, not auth state changes
    // Auth checks (user, loading, isInitialized, isAuthenticated) are guards inside the effect
    state.totalPoints, 
    state.currentWeekPoints, 
    state.activities.length, 
    state.virtualPet?.happiness, 
    state.simplePet?.happiness,
    state.simplePet?.stage,
    state.simplePet?.totalPoints,
    state.challenges.length,
    state.habits.length,
    state.lastStreakDate,
    state.weeklyGoal,
    user?.id, 
    setAppState
  ]);
  // Note: isInitialized, loading, and state.isAuthenticated are intentionally NOT in dependencies
  // They are checked as guards inside the effect but shouldn't trigger saves on their own
}